/**
 * PortalWhiteLabel — Per-account branding configuration
 *
 * Agencies can customize: brand name, logo, colors, custom domain,
 * and "powered by" visibility. Applied to widgets + landing pages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Paintbrush, Loader2, Save, Eye } from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface WhiteLabelConfig {
  id?: string;
  brand_name: string;
  brand_logo_url: string;
  primary_color: string;
  secondary_color: string;
  custom_domain: string;
  domain_verified: boolean;
  powered_by_visible: boolean;
  custom_css: string;
}

const DEFAULTS: WhiteLabelConfig = {
  brand_name: 'IntentDrive',
  brand_logo_url: '',
  primary_color: '#10b981',
  secondary_color: '#0f172a',
  custom_domain: '',
  domain_verified: false,
  powered_by_visible: true,
  custom_css: '',
};

export default function PortalWhiteLabel() {
  const { user } = useAuth();
  const [config, setConfig] = useState<WhiteLabelConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseGet(`whitelabel_config?user_id=eq.${user?.id}`);
      if (data && data.length > 0) {
        setConfig({ ...DEFAULTS, ...data[0] });
      }
    } catch (err) {
      console.error('Failed to load whitelabel config:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { if (user?.id) load(); }, [load, user?.id]);

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      if (config.id) {
        await supabasePatch(`whitelabel_config?id=eq.${config.id}`, {
          brand_name: config.brand_name,
          brand_logo_url: config.brand_logo_url,
          primary_color: config.primary_color,
          secondary_color: config.secondary_color,
          custom_domain: config.custom_domain,
          powered_by_visible: config.powered_by_visible,
          custom_css: config.custom_css,
        });
      } else {
        await supabasePost('whitelabel_config', { ...config, user_id: user.id });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      load();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const update = (key: keyof WhiteLabelConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Paintbrush className="h-6 w-6 text-emerald-400" />
              White Label
            </h1>
            <p className="text-sm text-slate-400 mt-1">Customize branding for your widgets and landing pages.</p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : saved ? <Save className="h-4 w-4 mr-1 text-emerald-300" /> : <Save className="h-4 w-4 mr-1" />}
            {saved ? 'Saved!' : 'Save'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : (
          <>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-sm text-slate-300">Brand Identity</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Brand Name</label>
                  <input value={config.brand_name} onChange={e => update('brand_name', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Logo URL</label>
                  <input value={config.brand_logo_url} onChange={e => update('brand_logo_url', e.target.value)} placeholder="https://..." className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Primary Color</label>
                    <div className="flex gap-2">
                      <input type="color" value={config.primary_color} onChange={e => update('primary_color', e.target.value)} className="w-10 h-10 rounded border border-slate-600 cursor-pointer" />
                      <input value={config.primary_color} onChange={e => update('primary_color', e.target.value)} className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Background Color</label>
                    <div className="flex gap-2">
                      <input type="color" value={config.secondary_color} onChange={e => update('secondary_color', e.target.value)} className="w-10 h-10 rounded border border-slate-600 cursor-pointer" />
                      <input value={config.secondary_color} onChange={e => update('secondary_color', e.target.value)} className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={config.powered_by_visible} onChange={e => update('powered_by_visible', e.target.checked)} className="rounded border-slate-600" />
                  <label className="text-sm text-slate-300">Show "Powered by" branding</label>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-sm text-slate-300">Custom Domain</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Domain</label>
                  <input value={config.custom_domain} onChange={e => update('custom_domain', e.target.value)} placeholder="app.yourbrand.com" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                {config.custom_domain && (
                  <div className={`px-3 py-2 rounded-lg text-xs ${config.domain_verified ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'}`}>
                    {config.domain_verified
                      ? 'Domain verified and active'
                      : 'Add a CNAME record pointing to intentdrive.pages.dev to verify your domain'}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-sm text-slate-300">Custom CSS (Advanced)</CardTitle></CardHeader>
              <CardContent>
                <textarea
                  value={config.custom_css}
                  onChange={e => update('custom_css', e.target.value)}
                  placeholder="/* Custom styles for your widgets */"
                  rows={6}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono"
                />
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader><CardTitle className="text-sm text-slate-300 flex items-center gap-1"><Eye className="h-4 w-4" /> Preview</CardTitle></CardHeader>
              <CardContent>
                <div className="rounded-xl border border-slate-700 overflow-hidden" style={{ background: config.secondary_color }}>
                  <div className="px-4 py-3 flex items-center gap-3" style={{ background: config.primary_color }}>
                    {config.brand_logo_url ? (
                      <img src={config.brand_logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">{config.brand_name.charAt(0)}</div>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-white">{config.brand_name}</div>
                      <div className="text-xs text-white/60">Online</div>
                    </div>
                  </div>
                  <div className="p-4 text-center text-sm text-slate-400">Widget preview area</div>
                  {config.powered_by_visible && (
                    <div className="px-4 py-2 border-t border-slate-700/50 text-right text-[10px] text-slate-600">Powered by {config.brand_name}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
